import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { CaretLeft, FloppyDisk, Eye, Archive, PaperPlaneTilt, ImageSquare } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import MarkdownEditor from "@/components/cms/MarkdownEditor";
import MediaBrowser from "@/components/cms/MediaBrowser";
import {
  createPage,
  getPage,
  PAGE_STATUS_META,
  slugify,
  updatePage,
} from "@/lib/cms/pages";

export default function CmsPageEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const isNew = !id || id === "new";
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [row, setRow] = useState(null);
  const [form, setForm] = useState({ title: "", slug: "", body_md: "", status: "draft" });
  const [slugLocked, setSlugLocked] = useState(!isNew);
  const [mediaOpen, setMediaOpen] = useState(false);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      try {
        const r = await getPage(id);
        if (!r) {
          toast.error("Page not found");
          navigate("/cms/pages");
          return;
        }
        setRow(r);
        setForm({ title: r.title || "", slug: r.slug || "", body_md: r.body_md || "", status: r.status });
      } catch (err) {
        toast.error(err.message || "Could not load page");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isNew, navigate]);

  const onTitleChange = (t) => {
    setForm((prev) => ({
      ...prev,
      title: t,
      slug: slugLocked ? prev.slug : slugify(t),
    }));
  };

  const save = async (nextStatus) => {
    if (!form.title.trim()) return toast.error("Title is required");
    if (!form.slug.trim()) return toast.error("Slug is required");
    setSaving(true);
    try {
      const status = nextStatus ?? form.status;
      if (isNew) {
        const created = await createPage({
          organizationId: profile?.organization_id,
          title: form.title.trim(),
          slug: form.slug.trim(),
          bodyMd: form.body_md,
          status,
        });
        toast.success(status === "published" ? "Published" : "Saved");
        navigate(`/cms/pages/${created.id}`, { replace: true });
      } else {
        const updated = await updatePage(id, {
          title: form.title.trim(),
          slug: form.slug.trim(),
          body_md: form.body_md,
          status,
        });
        setRow(updated);
        setForm({ title: updated.title, slug: updated.slug, body_md: updated.body_md || "", status: updated.status });
        toast.success(status === "published" ? "Published" : status === "archived" ? "Archived" : "Saved");
      }
    } catch (err) {
      if (err.code === "23505") {
        toast.error("Another page already uses this slug. Pick a different one.");
      } else {
        toast.error(err.message || "Save failed");
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
      </div>
    );
  }

  const statusMeta = PAGE_STATUS_META[form.status] || PAGE_STATUS_META.draft;
  const canPublish = form.status !== "published";
  const canUnpublish = form.status === "published";
  const canArchive = form.status !== "archived";

  return (
    <div data-testid="cms-page-editor" className="space-y-5">
      <Link
        to="/cms/pages"
        data-testid="cms-page-editor-back"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-600 hover:text-neutral-900"
      >
        <CaretLeft size={16} weight="bold" />
        All pages
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <label htmlFor="cms-page-editor-title" className="sr-only">Page title</label>
          <input
            id="cms-page-editor-title"
            data-testid="cms-page-editor-title"
            value={form.title}
            onChange={(e) => onTitleChange(e.target.value)}
            placeholder="Page title"
            className="w-full border-0 bg-transparent p-0 font-heading text-3xl font-semibold tracking-tight text-neutral-900 placeholder:text-neutral-300 focus:outline-none focus:ring-0 sm:text-4xl"
          />
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-neutral-500">
            <span className="text-xs">/</span>
            <label htmlFor="cms-page-editor-slug" className="sr-only">Page URL slug</label>
            <input
              id="cms-page-editor-slug"
              data-testid="cms-page-editor-slug"
              value={form.slug}
              onChange={(e) => { setSlugLocked(true); setForm({ ...form, slug: slugify(e.target.value) }); }}
              placeholder="slug"
              className="rounded-md border border-dashed border-neutral-300 bg-white px-2 py-0.5 font-mono text-xs focus:border-primary focus:outline-none"
            />
            <span
              data-testid="cms-page-editor-status"
              className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${statusMeta.tone}`}
            >
              {statusMeta.label}
            </span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <button
            type="button"
            data-testid="cms-page-editor-media"
            onClick={() => setMediaOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-2 text-xs font-semibold text-neutral-600 hover:bg-neutral-50"
          >
            <ImageSquare size={12} weight="duotone" />
            Media
          </button>
          <button
            type="button"
            data-testid="cms-page-editor-save"
            disabled={saving}
            onClick={() => save()}
            className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover disabled:opacity-60"
          >
            <FloppyDisk size={12} weight="duotone" />
            {saving ? "Saving…" : "Save"}
          </button>
          {canPublish && (
            <button
              type="button"
              data-testid="cms-page-editor-publish"
              disabled={saving}
              onClick={() => save("published")}
              className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/5 px-3 py-2 text-xs font-semibold text-primary-hover hover:bg-primary/10 disabled:opacity-60"
            >
              <PaperPlaneTilt size={12} weight="duotone" />
              Publish
            </button>
          )}
          {canUnpublish && (
            <button
              type="button"
              data-testid="cms-page-editor-unpublish"
              disabled={saving}
              onClick={() => save("draft")}
              className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-2 text-xs font-semibold text-neutral-600 hover:bg-neutral-50 disabled:opacity-60"
            >
              <Eye size={12} weight="duotone" />
              Unpublish
            </button>
          )}
          {canArchive && !isNew && (
            <button
              type="button"
              data-testid="cms-page-editor-archive"
              disabled={saving}
              onClick={() => save("archived")}
              className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-2 text-xs font-semibold text-neutral-600 hover:bg-neutral-50 disabled:opacity-60"
            >
              <Archive size={12} weight="duotone" />
              Archive
            </button>
          )}
        </div>
      </header>

      <MarkdownEditor
        value={form.body_md}
        onChange={(v) => setForm({ ...form, body_md: v })}
        organizationId={profile?.organization_id}
        testIdPrefix="cms-page-editor-body"
        minRows={18}
        placeholder="# Welcome to Little Acorns…"
      />

      {row?.updated_at && !isNew && (
        <p className="text-right text-[11px] text-neutral-500">
          Last edited {new Date(row.updated_at).toLocaleString()}
        </p>
      )}

      {mediaOpen && (
        <MediaBrowser
          organizationId={profile?.organization_id}
          mode="manage"
          onClose={() => setMediaOpen(false)}
        />
      )}
    </div>
  );
}
